import {
  Image as BaseImage,
  Box,
  ImageProps as BaseImageProps,
  useBoolean,
  Center,
  BoxProps,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getImageS3Url } from "utils/file";
import Spinner from "./ui/Spinner";
import { DEFAULT_IMAGE_NOT_FOUND } from "constants/default";
import { isUndefined } from "lodash-es";
import { axiosECS } from "services/baseAxios";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";

interface ImageProps extends BaseImageProps {
  showLoading?: boolean;
  isSelfContainer?: boolean;
  containerProps?: BoxProps;
  fallbackSrc?: string;
  isS3?: boolean;
}

const Image = React.forwardRef<any, ImageProps>(
  (
    {
      src,
      showLoading = true,
      isSelfContainer = false,
      fallbackSrc = DEFAULT_IMAGE_NOT_FOUND,
      onLoad,
      onError,
      containerProps,
      isS3 = true,
      ...other
    },
    ref
  ) => {
    const [isLoading, loading] = useBoolean(showLoading);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [isReload, setIsReload] = useState(false);
    const { token } = useSelector((state: RootState) => state.user);
    const isRetriedRef = useRef(false);

    const setFallbackError = useCallback(
      (e: any) => {
        setPath(fallbackSrc);
        loading.off();
        onError?.(e);
      },
      [fallbackSrc, loading, onError]
    );

    const handleError = useCallback(
      async (e: any) => {
        if (isRetriedRef.current) {
          setFallbackError(e);

          return;
        }

        isRetriedRef.current = true;

        try {
          const response = await axiosECS.get(path!, {
            headers: {
              isIgnoreShowMessageError: true,
            },
          });
          if (response.errorCode === 404) {
            setFallbackError(e);

            return;
          }
        } catch (error) {
          /** Ignore error */
        }

        setPath("");
        setIsReload(true);
      },
      [path, setFallbackError]
    );

    const handleLoad = useCallback(
      (e: any) => {
        onLoad?.(e);
        loading.off();
        setIsReload(false);
      },
      [loading, onLoad]
    );

    useEffect(() => {
      if (token && isReload && src) {
        setPath(getImageS3Url(src));
      }
    }, [token, isReload, src]);

    useEffect(() => {
      loading.on();
      setPath(src ? getImageS3Url(src) : "");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    const image = !isUndefined(path) ? (
      <BaseImage
        {...other}
        src={path || fallbackSrc}
        onLoad={handleLoad}
        onError={handleError}
      />
    ) : (
      <></>
    );

    const spinner = (
      <Box position="absolute" inset="0px" minH="3rem">
        <Center
          position="absolute"
          zIndex="docked"
          top={0}
          left={0}
          w="100%"
          h="100%"
          backgroundColor="#FAFAFA"
          minW="3rem"
          minH="3rem"
        >
          <Spinner />
        </Center>
      </Box>
    );

    if (!isS3) {
      return (
        <BaseImage {...other} src={src} onLoad={onLoad} onError={onError} />
      );
    }

    if (!showLoading) {
      return image;
    }

    if (isSelfContainer) {
      return (
        <>
          {image}
          {isLoading && spinner}
        </>
      );
    }

    return (
      <Box
        position={"relative"}
        h={"100%"}
        w={"100%"}
        minH={isLoading ? "3rem" : "auto"}
        sx={{
          img: {
            maxW: "100%",
            maxH: "100%",
          },
        }}
        {...containerProps}
      >
        {image}
        {isLoading && spinner}
      </Box>
    );
  }
);

export default Image;
